import { useEffect, useState } from "react";
import { Crown, RefreshCw, Wallet, TrendingUp, Users, Gift } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import PageHeader from "../components/PageHeader";

function fmt(n) {
  return Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });
}

export default function VipDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [recomputing, setRecomputing] = useState(false);
  const [granting, setGranting] = useState(null); // user_id being granted to
  const [grantAmount, setGrantAmount] = useState("");
  const [grantNote, setGrantNote] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/vip/overview");
      setData(data);
    } catch {
      toast.error("Failed to load VIP overview");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const recompute = async () => {
    if (!window.confirm("Recompute every player's VIP tier from their lifetime turnover?")) return;
    setRecomputing(true);
    try {
      const { data } = await api.post("/admin/vip/recompute-all");
      toast.success(`Recomputed: ${data.updated} player(s) tier updated`);
      await load();
    } catch {
      toast.error("Recompute failed");
    } finally {
      setRecomputing(false);
    }
  };

  const grant = async () => {
    const amt = parseFloat(grantAmount);
    if (!amt || amt <= 0) return toast.error("Amount must be > 0");
    try {
      await api.post(`/admin/vip/${granting}/grant-cashback`, { amount: amt, note: grantNote || null });
      toast.success(`Credited ৳${amt.toFixed(2)} to player`);
      setGranting(null);
      setGrantAmount("");
      setGrantNote("");
      await load();
    } catch {
      toast.error("Grant failed");
    }
  };

  const totals = data?.totals || {};
  const dist = data?.distribution || [];
  const top = data?.top_players || [];

  return (
    <div data-testid="vip-dashboard-page">
      <PageHeader
        title="VIP / Loyalty Dashboard"
        subtitle="Tier distribution · cashback liability · top players · auto-rakeback engine"
      />

      <div className="flex justify-end mb-4">
        <button
          type="button"
          onClick={recompute}
          disabled={recomputing}
          className="h-9 px-3 bg-gray-900 hover:bg-gray-800 text-white rounded-sm text-xs font-semibold inline-flex items-center gap-1.5 disabled:opacity-60"
          data-testid="vip-recompute-btn"
        >
          <RefreshCw size={14} className={recomputing ? "animate-spin" : ""} />
          {recomputing ? "Recomputing…" : "Recompute all tiers"}
        </button>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Stat icon={Users} label="Active VIP players" value={fmt(totals.vip_player_count)} hint="Above Normal tier" testId="vip-stat-players" />
        <Stat icon={Wallet} label="Pending cashback" value={`৳${fmt(totals.cashback_pending)}`} hint="Liability across all players" tone="warn" testId="vip-stat-pending" />
        <Stat icon={Gift} label="Lifetime cashback paid" value={`৳${fmt(totals.cashback_claimed_lifetime)}`} hint="Successfully claimed" tone="ok" testId="vip-stat-claimed" />
        <Stat icon={TrendingUp} label="Total lifetime turnover" value={`৳${fmt(totals.lifetime_turnover_all)}`} hint="All players combined" testId="vip-stat-turnover" />
      </div>

      {/* Tier distribution */}
      <div className="bg-white border border-gray-200 rounded-sm mb-6">
        <div className="px-5 py-3 border-b border-gray-100 flex items-center gap-2">
          <Crown size={14} className="text-emerald-500" />
          <div className="text-[11px] uppercase tracking-widest text-gray-500 font-semibold">Tier distribution</div>
        </div>
        {loading ? (
          <div className="p-6 text-sm text-gray-500 text-center">Loading…</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="vip-distribution-table">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr className="text-left text-[11px] uppercase tracking-wider text-gray-500">
                  <th className="px-5 py-2.5 font-semibold">Tier</th>
                  <th className="px-5 py-2.5 font-semibold">Min turnover</th>
                  <th className="px-5 py-2.5 font-semibold">Rakeback</th>
                  <th className="px-5 py-2.5 font-semibold">Cashback</th>
                  <th className="px-5 py-2.5 font-semibold text-right">Players</th>
                </tr>
              </thead>
              <tbody>
                {dist.map((d) => (
                  <tr key={d.name} className="border-b border-gray-50 last:border-0" data-testid={`vip-dist-${d.name}`}>
                    <td className="px-5 py-2.5 flex items-center gap-2">
                      <span className="h-5 w-5 rounded-md inline-block" style={{ background: `linear-gradient(135deg, ${d.color} 0%, #1a2030 100%)` }} />
                      <span className="font-semibold text-gray-800">{d.name}</span>
                    </td>
                    <td className="px-5 py-2.5 text-gray-600 tabular-nums">৳{fmt(d.min_turnover)}</td>
                    <td className="px-5 py-2.5 text-gray-600 tabular-nums">{Number(d.rakeback_pct || 0).toFixed(2)}%</td>
                    <td className="px-5 py-2.5 text-gray-600 tabular-nums">{Number(d.cashback_pct || 0).toFixed(2)}%</td>
                    <td className="px-5 py-2.5 text-right tabular-nums font-semibold text-gray-900">{fmt(d.count)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Top players */}
      <div className="bg-white border border-gray-200 rounded-sm">
        <div className="px-5 py-3 border-b border-gray-100 flex items-center gap-2">
          <TrendingUp size={14} className="text-emerald-500" />
          <div className="text-[11px] uppercase tracking-widest text-gray-500 font-semibold">Top 10 players by lifetime turnover</div>
        </div>
        {loading ? (
          <div className="p-6 text-sm text-gray-500 text-center">Loading…</div>
        ) : top.length === 0 ? (
          <div className="p-6 text-sm text-gray-500 text-center">No turnover yet. As bets stream in, this list will populate.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="vip-top-players-table">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr className="text-left text-[11px] uppercase tracking-wider text-gray-500">
                  <th className="px-5 py-2.5 font-semibold">#</th>
                  <th className="px-5 py-2.5 font-semibold">Player</th>
                  <th className="px-5 py-2.5 font-semibold">Tier</th>
                  <th className="px-5 py-2.5 font-semibold text-right">Lifetime turnover</th>
                  <th className="px-5 py-2.5 font-semibold text-right">Pending cashback</th>
                  <th className="px-5 py-2.5 font-semibold text-right">Claimed lifetime</th>
                  <th className="px-5 py-2.5 font-semibold text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {top.map((p, i) => (
                  <tr key={p.id} className="border-b border-gray-50 last:border-0" data-testid={`vip-top-row-${i}`}>
                    <td className="px-5 py-2.5 text-gray-500 tabular-nums">{i + 1}</td>
                    <td className="px-5 py-2.5">
                      <div className="font-semibold text-gray-800">{p.username || "—"}</div>
                      <div className="text-[11px] text-gray-400">ID: {p.player_id || p.id?.slice(0, 8)}</div>
                    </td>
                    <td className="px-5 py-2.5">
                      <span className="inline-flex items-center gap-1.5 px-2 h-6 rounded-sm text-[11px] font-semibold" style={{ background: "rgba(16,185,129,0.08)", color: "#059669" }}>
                        <Crown size={11} /> {p.vip_tier || "Normal"}
                      </span>
                    </td>
                    <td className="px-5 py-2.5 text-right tabular-nums font-semibold text-gray-900">৳{fmt(p.lifetime_turnover)}</td>
                    <td className="px-5 py-2.5 text-right tabular-nums text-amber-700">৳{fmt(p.cashback_pending)}</td>
                    <td className="px-5 py-2.5 text-right tabular-nums text-emerald-700">৳{fmt(p.cashback_claimed_total)}</td>
                    <td className="px-5 py-2.5 text-right">
                      <button
                        type="button"
                        onClick={() => { setGranting(p.id); setGrantAmount(""); setGrantNote(""); }}
                        className="h-8 px-3 border border-gray-200 hover:border-emerald-400 hover:text-emerald-600 text-gray-700 rounded-sm text-[11px] font-semibold"
                        data-testid={`vip-grant-btn-${p.id}`}
                      >
                        Grant cashback
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Grant modal */}
      {granting && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setGranting(null)}>
          <div className="bg-white rounded-sm w-full max-w-md p-5" onClick={(e) => e.stopPropagation()} data-testid="vip-grant-modal">
            <div className="text-sm font-semibold text-gray-800 mb-1">Grant cashback bonus</div>
            <div className="text-xs text-gray-500 mb-4">Credits the amount to the player's pending cashback. They'll be notified and can claim from their VIP page.</div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1.5">Amount (৳)</label>
            <input type="number" step="0.01" autoFocus value={grantAmount} onChange={(e) => setGrantAmount(e.target.value)} className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm mb-3" data-testid="vip-grant-amount" />
            <label className="block text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1.5">Note (optional)</label>
            <input value={grantNote} onChange={(e) => setGrantNote(e.target.value)} className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm mb-4" placeholder="e.g. Loyalty appreciation" data-testid="vip-grant-note" />
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setGranting(null)} className="h-9 px-4 border border-gray-200 hover:bg-gray-50 rounded-sm text-xs font-semibold text-gray-700">Cancel</button>
              <button type="button" onClick={grant} className="h-9 px-4 bg-emerald-500 hover:bg-emerald-600 text-white rounded-sm text-xs font-semibold" data-testid="vip-grant-confirm">Credit cashback</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ icon: Icon, label, value, hint, tone = "default", testId }) {
  const toneCls = tone === "warn" ? "text-amber-600 bg-amber-50" : tone === "ok" ? "text-emerald-600 bg-emerald-50" : "text-gray-700 bg-gray-50";
  return (
    <div className="bg-white border border-gray-200 rounded-sm p-4" data-testid={testId}>
      <div className="flex items-center gap-2 mb-2">
        <span className={`h-7 w-7 rounded-sm inline-flex items-center justify-center ${toneCls}`}>
          <Icon size={14} />
        </span>
        <div className="text-[11px] uppercase tracking-widest text-gray-500 font-semibold">{label}</div>
      </div>
      <div className="text-2xl font-extrabold text-gray-900 tabular-nums">{value}</div>
      <div className="text-[11px] text-gray-400 mt-0.5">{hint}</div>
    </div>
  );
}
